import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/lib/LanguageContext";
import { calculatePrice, MIN_SLIDER_DOMAINS, MAX_SLIDER_DOMAINS, SLIDER_STEP } from "@/lib/pricing";

const PricingSection = ({ onSelectTier }) => {
  const { t } = useLanguage();
  const [activeTier, setActiveTier] = useState("full");
  const [domainCount, setDomainCount] = useState(MIN_SLIDER_DOMAINS);

  const price = useMemo(
    () => calculatePrice(activeTier, domainCount),
    [activeTier, domainCount]
  );

  const selectAndScroll = () => {
    if (onSelectTier) onSelectTier(activeTier);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const adFeatures = [
    { key: "pricing.ad.feature1", included: true },
    { key: "pricing.ad.feature2", included: true },
    { key: "pricing.ad.feature3", included: true },
    { key: "pricing.ad.noEmail", included: false },
  ];

  const fullFeatures = [
    { key: "pricing.full.feature1", included: true },
    { key: "pricing.full.feature2", included: true },
    { key: "pricing.full.feature3", included: true },
    { key: "pricing.full.feature5", included: true },
    { key: "pricing.full.feature4", included: true },
  ];

  const features = activeTier === "full" ? fullFeatures : adFeatures;

  const domainLabel =
    domainCount <= MIN_SLIDER_DOMAINS
      ? t("pricing.domainsUpTo", { count: MIN_SLIDER_DOMAINS })
      : t("pricing.domains", { count: domainCount.toLocaleString() });

  return (
    <section className="max-w-7xl mx-auto px-6 py-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4"
          style={{ fontFamily: "Inter, sans-serif", letterSpacing: "-0.03em" }}
        >
          {t("pricing.title.before")}{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">
            {t("pricing.title.highlight")}
          </span>{" "}
          {t("pricing.title.after")}
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="max-w-5xl mx-auto"
      >
        <div className="glass-card rounded-2xl">
          <div className="grid md:grid-cols-2">
            {/* Left: controls */}
            <div className="relative z-10 p-6 sm:p-8 md:p-10 flex flex-col">
              {/* Tab toggle */}
              <div className="inline-flex bg-white/5 rounded-xl p-1 mb-8 self-start">
                <button
                  type="button"
                  onClick={() => setActiveTier("ad")}
                  className={`relative z-20 px-4 sm:px-5 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    activeTier === "ad"
                      ? "btn-accent text-black shadow-lg"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {t("pricing.tab.ad")}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTier("full")}
                  className={`relative z-20 px-4 sm:px-5 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer flex items-center gap-1.5 sm:gap-2 ${
                    activeTier === "full"
                      ? "btn-accent text-black shadow-lg"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  <span className="whitespace-nowrap">{t("pricing.tab.full")}</span>
                  <span
                    className={`text-[9px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full leading-tight whitespace-nowrap ${
                      activeTier === "full"
                        ? "bg-black/20 text-black"
                        : "bg-yellow-400/20 text-yellow-400"
                    }`}
                  >
                    {t("pricing.bestValue")}
                  </span>
                </button>
              </div>

              {/* Price display */}
              <div className="mb-8">
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl sm:text-6xl font-black text-white">
                    {Math.floor(price)}
                  </span>
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold text-white/60">CHF</span>
                    <span className="text-sm text-white/40">
                      /{t("pricing.perUpload")}
                    </span>
                  </div>
                </div>
                {price % 1 !== 0 && (
                  <p className="text-white/50 text-sm mt-1">
                    {t("pricing.totalPrice")} {price.toFixed(2)} CHF
                  </p>
                )}
              </div>

              {/* Slider */}
              <div className="relative z-20 mb-8">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-white/70">
                    {domainLabel}
                  </span>
                </div>
                <Slider
                  value={[domainCount]}
                  onValueChange={(v) => setDomainCount(v[0])}
                  min={MIN_SLIDER_DOMAINS}
                  max={MAX_SLIDER_DOMAINS}
                  step={SLIDER_STEP}
                  className="w-full"
                />
                <div className="relative mt-2 text-xs text-white/30 h-4">
                  <span className="absolute left-0">100</span>
                  <span className="absolute -translate-x-1/2" style={{ left: "18.4%" }}>1,000</span>
                  <span className="absolute -translate-x-1/2" style={{ left: "59.2%" }}>3,000</span>
                  <span className="absolute right-0">5,000</span>
                </div>
              </div>

              {/* CTA */}
              <Button
                type="button"
                onClick={selectAndScroll}
                className="relative z-20 w-full py-6 rounded-xl btn-accent font-bold text-base cursor-pointer"
              >
                {t("pricing.cta")}
              </Button>

              <p className="text-xs text-center text-white/30 mt-3">
                {t("pricing.contactSales")}
              </p>
            </div>

            {/* Right: feature list */}
            <div className="p-6 sm:p-8 md:p-10 border-t md:border-t-0 md:border-l border-white/10 flex flex-col justify-center">
              <p className="text-xs font-bold text-white/40 tracking-widest mb-6">
                {t("pricing.includes")}
              </p>
              <ul className="space-y-4">
                {features.map((f) => (
                  <li key={f.key} className="flex items-center gap-3">
                    {f.included ? (
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-white/20 flex-shrink-0" />
                    )}
                    <span
                      className={
                        f.included ? "text-white/70" : "text-white/40"
                      }
                    >
                      {t(f.key)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default PricingSection;
